import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Provider } from 'react-redux'; // 11KB
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Provider as UnstatedProvider } from 'unstated';

import AtlaskitAnalyticsListener from '@atlaskit/analytics-listeners';

import { PostOfficeProvider } from '@atlassian/confluence-post-office-provider';
import { ScreenSpaceFlagsPlacement } from '@atlassian/confluence-post-office-provider/entry-points/screen-space-flags-placement';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';

import { GraphqlProvider } from '@confluence/graphql-provider/entry-points/graphqlProvider.ts';
import { NavdexListener } from '@confluence/navdex/entry-points/NavdexListener.tsx';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	/* eslint-disable-next-line no-restricted-imports */
	getExperienceTracker,
} from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { SessionDataContext } from '@confluence/session-data/entry-points/SessionDataContext.ts';
import { I18nProvider } from '@confluence/i18n/entry-points/I18nProvider.ts';
import { CrossFlowProvider } from '@confluence/cross-flow';
import { UpFlowProvider } from '@confluence/change-edition/entry-points/UpFlowProvider.ts';
import { ConfluenceAnalyticsListener } from '@confluence/analytics/entry-points/analyticsListener.ts';
import { SPAViewContextProvider } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { CoreInvitesProvider } from '@confluence/core-invites-provider/entry-points/CoreInvitesProvider.ts';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { ContentStateStateContainer } from '@confluence/content-state/entry-points/ContentStateStateContainer.ts';
import { EmojiTitleStateContainer } from '@confluence/emoji-title/entry-points/emojiTitleStateContainer.ts';
import { ReattachCommentProvider } from '@confluence/comment-context/entry-points/ReattachCommentContext.ts';
import { ChoreographerAssetsContextProvider } from '@confluence/choreographer-services/entry-points/ChoreographerAssetsContext.ts';
import { XProductUserInviteProvider } from '@confluence/fabric-media-support/entry-points/MentionsProvider.ts';

import { EngagementProvider } from '../EngagementProvider/index.ts';

import { App } from './App.jsx';

import { getDefaultRelayEnvironmentConfig } from '@atlassian/relay-environment-config';
import { fg } from '@confluence/feature-gating';
import { Environment } from 'relay-runtime';

const defaultRelayEnvironment = new Environment(getDefaultRelayEnvironmentConfig({}));

export default class Root extends Component {
	render() {
		window.performance.mark('CFP-63.root-component-render');

		const { store, apolloClient, sessionData } = this.props;

		return (
			// This is at the top because the root-level UnstatedProvider must not re-render
			// otherwise it will lose and recreate its instances
			<UnstatedProvider
				inject={[
					new BannerStateContainer({
						initialState: {
							banners: window.__SSR_BANNERS__,
						},
					}),
					new ContentStateStateContainer(window.__SSR_CONTENT_STATE__),
					new EmojiTitleStateContainer(window.__SSR_EMOJI_TITLE__),
				]}
			>
				<GraphqlProvider
					client={apolloClient}
					relayEnvironment={fg('cfe_use_default_relay_env') ? defaultRelayEnvironment : undefined}
				>
					<Provider store={store}>
						<SessionDataContext.Provider value={sessionData}>
							<ChoreographerAssetsContextProvider>
								<SPAViewContextProvider>
									<ExperienceTrackerContext.Provider value={getExperienceTracker()}>
										<I18nProvider>
											<CrossFlowProvider>
												<AtlaskitAnalyticsListener client={getAnalyticsWebClient()}>
													<NavdexListener>
														<EngagementProvider>
															<ConfluenceAnalyticsListener>
																<UpFlowProvider>
																	<CoreInvitesProvider>
																		<XProductUserInviteProvider>
																			<ReattachCommentProvider>
																				<PostOfficeProvider>
																					<App sessionData={sessionData} />
																					<ScreenSpaceFlagsPlacement />
																				</PostOfficeProvider>
																			</ReattachCommentProvider>
																		</XProductUserInviteProvider>
																	</CoreInvitesProvider>
																</UpFlowProvider>
															</ConfluenceAnalyticsListener>
														</EngagementProvider>
													</NavdexListener>
												</AtlaskitAnalyticsListener>
											</CrossFlowProvider>
										</I18nProvider>
									</ExperienceTrackerContext.Provider>
								</SPAViewContextProvider>
							</ChoreographerAssetsContextProvider>
						</SessionDataContext.Provider>
					</Provider>
				</GraphqlProvider>
			</UnstatedProvider>
		);
	}
}

Root.displayName = 'Root';

Root.propTypes = {
	/**
	 * Global store
	 */
	store: PropTypes.object.isRequired,
	apolloClient: PropTypes.object.isRequired,
	sessionData: PropTypes.object.isRequired,
};
